import { Eye } from "lucide-react";
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";


const VIEWPORT_MARGIN = 8;
const GAP = 8;

// Small hover label. Rendered in a portal so the scrolling columns can't clip it,
// and the shared Tooltip's heavy bubble isn't needed for a one-line hint.
const VisibilityHint = ({ label, size = 14 }) => {
    const [anchor, setAnchor] = useState(null);
    const [position, setPosition] = useState(null);
    const iconRef = useRef(null);
    const tooltipRef = useRef(null);

    // Measured before paint so the label can be clamped to the viewport without a flicker.
    useLayoutEffect(() => {
        if (!anchor || !tooltipRef.current) return;
        const { width, height } = tooltipRef.current.getBoundingClientRect();
        const centered = anchor.left + anchor.width / 2 - width / 2;
        const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
        const left = Math.max(VIEWPORT_MARGIN, Math.min(centered, maxLeft));
        const above = anchor.top - GAP - height;
        const top = above >= VIEWPORT_MARGIN ? above : anchor.bottom + GAP;
        setPosition({ left, top });
    }, [anchor]);

    const show = () => {
        const rect = iconRef.current?.getBoundingClientRect();
        if (rect) setAnchor(rect);
    };

    const hide = () => {
        setAnchor(null);
        setPosition(null);
    };

    return (
        <span
            ref={iconRef}
            className="inline-flex"
            onMouseEnter={show}
            onMouseLeave={hide}
        >
            <Eye className="text-sky-600" size={size} aria-label={label} />
            {anchor &&
                createPortal(
                    <span
                        ref={tooltipRef}
                        role="tooltip"
                        style={position ?? { left: 0, top: 0, visibility: "hidden" }}
                        className="pointer-events-none fixed z-[9999] whitespace-nowrap rounded-md bg-gray-800 px-2 py-1 text-body leading-none text-white shadow-md"
                    >
                        {label}
                    </span>,
                    document.body
                )}
        </span>
    );
};

export default VisibilityHint;
